import jsPDF from "jspdf";

import { IPdfDataObject, IPdfOptions } from "@src/app/interfaces/pdf.interface";
import { PdfImageElement } from "./pdf-image-element";
import { PdfTextElement } from "./pdf-text-element";
import { PdfTableElement } from "./pdf-table-element";
import { PdfSpaceElement } from "./pdf-space-element";

export class PdfGenerator {

  private elements: IPdfDataObject[];
  private options: IPdfOptions;
  private logoUrl: string;

  constructor(elements: IPdfDataObject[], options: IPdfOptions, logoUrl: string = '/assets/img/tekom-logo-black.png') {
    this.elements = elements;
    this.options = options;
    this.logoUrl = logoUrl;
  }

  static createElement(item: IPdfDataObject) {
    if (item.image) {
      return new PdfImageElement(item.image, item.newPage);
    }
    else if (item.text) {
      return new PdfTextElement(item.text, item.newPage);
    }
    else if (item.table) {
      return new PdfTableElement(item, item.newPage);
    }
    else if (item.space) {
      return new PdfSpaceElement(item.space.width, item.space.height, item.newPage);
    }
    throw new Error();
  }

  private async addHeadersElementInNewPage(doc: jsPDF, currentY: number, maxWidth: number): Promise<number> {
    let y = currentY;
    const headers = this.elements.filter(item => item.isHeaderNewPage).map(item => PdfGenerator.createElement(item));
    this.addLogo(doc);

    for (const element of headers) {
      const usedHeight = await element.render(doc, this.options.sideMargin, y, maxWidth);
      y += usedHeight + this.options.spacing;
    }

    return y;
  }

  private addFooter(doc: jsPDF): void {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageNumber = doc.getNumberOfPages();
  
    doc.setFontSize(10);
    doc.text(`${pageNumber}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
  }

  public async generatePdf(): Promise<void> {
    const doc = new jsPDF({ unit: "px" });
    doc.setFontSize(11);

    const pageWidth = doc.internal.pageSize.getWidth();
    const maxWidth = pageWidth - this.options.sideMargin * 2;

    const sortedElements = this.elements.sort((a, b) => a.index - b.index).filter(item => !item.isHeaderNewPage);
    const pdfElements = sortedElements.map(item => PdfGenerator.createElement(item));

    let currentY = this.options.topMargin;

    const headerHeight = await this.addHeadersElementInNewPage(doc, currentY, maxWidth); 
    currentY += headerHeight;

    for (const element of pdfElements) {
      try {
        if (element.newPage) {
          doc.addPage();
          currentY = this.options.topMargin;
          const headerHeight = await this.addHeadersElementInNewPage(doc, currentY, maxWidth); 
          currentY += headerHeight;
        }

        const usedHeight = await element.render(doc, this.options.sideMargin, currentY, maxWidth);
        currentY += usedHeight + this.options.spacing;

        const pageHeight = doc.internal.pageSize.getHeight();
        if (currentY > pageHeight) {
          doc.addPage();
          currentY = this.options.topMargin;
          const headerHeight = await this.addHeadersElementInNewPage(doc, currentY, maxWidth); 
          currentY += headerHeight;
        }
        this.addFooter(doc);
      }
      catch (err) {
        console.error("Błąd podczas renderowania elementu: ", err);
      }
    }

    this.addFooter(doc);
    doc.save(this.options.fileName);
  }

  private addLogo(doc: jsPDF): void {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.addImage(this.logoUrl, 'PNG', pageWidth - 100, 10, 90, 30);
  }

}
